import { EFinancingCompany } from '@if/core/types'
import { useIfUser } from '@if/mui'
import { saveLastCompany, useLastCompany } from '@src/services/company-hooks'
import { replaceCompany } from '@src/services/company-path'
import { useEffect } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'

/**
 * Guards every screen under `/:company`. A company the user does not belong to — unknown, mistyped, or simply
 * not theirs — sends them to the same screen in their last company, so the screens below can trust the URL's.
 */
const CompanyRoute = () => {
  const { company } = useParams()
  const { companyIds } = useIfUser()
  const lastCompany = useLastCompany()
  const { pathname } = useLocation()
  const isUserCompany = companyIds.includes(company as EFinancingCompany)

  useEffect(() => {
    if (isUserCompany) saveLastCompany(company as EFinancingCompany)
  }, [isUserCompany, company])

  if (isUserCompany) return <Outlet />
  // A user without any company has nowhere to be sent.
  if (companyIds.length === 0) return null
  return <Navigate to={replaceCompany(pathname, lastCompany)} replace />
}

/**
 * Sends a company-less URL — `/`, `/loans`, an old bookmark — to the same screen in the URL's company, or in the
 * last one when the URL has none.
 */
export const CompanyRedirect = ({ to }: { to: string }) => {
  const { company } = useParams()
  const { companyIds } = useIfUser()
  const lastCompany = useLastCompany()
  const target = companyIds.includes(company as EFinancingCompany) ? (company as EFinancingCompany) : lastCompany
  return <Navigate to={`/${target}/${to}`} replace />
}

export default CompanyRoute
